/**
 * "Import from Google Drive / OneDrive" for the customer asset library (/content/assets), via /v1/connectors/drive.
 * Hidden entirely while the feature flag assets.cloud_import is off (the API answers 404 feature_disabled).
 *
 * Connect: POST /oauth/start -> provider consent -> the worker's /bff/oauth/connectors/callback -> /integrations/connect
 * (ConnectCallbackPage posts `dos_` states to /v1/connectors/drive/oauth/callback) -> back here with ?drive=<provider>.
 * Pick: browse one folder at a time (metadata only), tick importable files, Import -> one queued job per file
 * (Idempotency-Key per selection, so a double click or a retry never imports twice). Progress polls /imports.
 *
 * Visibility (owner decision, 26 Sep 2026): the customer picks "Only me" or "Everyone in this workspace" when
 * connecting (default private, unchanged behaviour), and can flip it later from the connection row (PATCH
 * /connections/:id). A workspace-shared connection from a teammate shows up below as something to browse from,
 * not something to disconnect or reconfigure (the server enforces who may do what either way).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, DataTable, EmptyState, ErrorState, Heading, Loading, Text, Toggle, type Column } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff, BffError, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { safeAuthorizeUrl } from '../integrations/IntegrationsPage';

export const DRIVES = ['google_drive', 'onedrive'] as const;
export type Drive = (typeof DRIVES)[number];

/** Who besides the connecting user may browse/import/resync a connection (owner decision, 26 Sep 2026): the
 * customer's own choice, made at connect time and changeable any time from the connection row. */
export const DRIVE_VISIBILITIES = ['private', 'workspace'] as const;
export type DriveVisibility = (typeof DRIVE_VISIBILITIES)[number];

interface ConnectionView {
  id: string; provider: Drive; displayName: string | null; status: 'connected' | 'reconnect_needed' | 'revoked';
  mine: boolean; visibility: DriveVisibility; connectedBy?: string;
}
interface ProviderView { provider: Drive; configured: boolean; connection: ConnectionView | null }
interface ItemView { id: string; name: string; kind: 'folder' | 'file'; mimeType: string | null; sizeBytes: number | null; modifiedAt: string | null; importAs: string | null; importable: boolean; reason: string | null }
interface ImportView { id: string; provider: Drive; fileName: string; status: 'queued' | 'fetching' | 'completed' | 'rejected' | 'failed'; reason: string | null; assetId: string; createdAt: string }

const errorCode = (e: unknown): string | null => (e instanceof BffError ? ((e.envelope?.error.details as { code?: string } | undefined)?.code ?? e.envelope?.error.code ?? null) : null);

/** i18n key for an API failure on this screen. */
export function driveErrorKey(e: unknown): string {
  const code = errorCode(e);
  if (code === 'reconnect_needed') return 'connectors.drive.error.reconnect_needed';
  if (code === 'provider_not_configured') return 'connectors.drive.error.provider_not_configured';
  if (code === 'validation_failed') return 'connectors.drive.error.rejected';
  if (code === 'oauth_state_expired') return 'connectors.connect.state_expired';
  if (code === 'missing_scopes') return 'connectors.connect.missing_scopes';
  return 'connectors.drive.error.generic';
}

const KNOWN_REASONS = ['folder', 'unsupported_type', 'too_large', 'unknown_size', 'trashed', 'executable', 'content_mismatch', 'unknown_content', 'malware_detected',
  'disconnected', 'reconnect_needed', 'source_not_found', 'provider_rejected', 'empty_file', 'kind_mismatch'];
export const reasonKey = (r: string | null) => `connectors.drive.reason.${r && KNOWN_REASONS.includes(r) ? r : 'other'}`;

/** Human file size (1 decimal, binary units) with locale digits. */
export function sizeLabel(n: number | null, num: (n: number) => string): string {
  if (n == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n; let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u += 1; }
  return `${num(Math.round(v * 10) / 10)} ${units[u]}`;
}

/** Idempotency-Key for one selection: kept while the selection is unchanged (a retry or double click replays), renewed on change. */
export const newImportKey = () => `drive-import-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function DriveImport({ connected, onImported }: { connected: string | null; onImported: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const providers = useResource<ProviderView[]>('/v1/connectors/drive/providers');
  const connections = useResource<ConnectionView[]>('/v1/connectors/drive/connections');
  const [open, setOpen] = useState<ConnectionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<Drive | null>(null);
  const [importsTick, setImportsTick] = useState(0);
  const [visibility, setVisibility] = useState<DriveVisibility>('private');

  // Feature off (404) or not loaded yet: render nothing, so the library works exactly as before.
  if (providers.status !== 'ready') return null;
  const list = providers.data;
  const shared = connections.status === 'ready' ? connections.data.filter((c) => !c.mine) : [];

  const connect = async (provider: Drive) => {
    setError(null); setBusy(provider);
    try {
      const r = await bffJson<{ authorizeUrl: string }>('/v1/connectors/drive/oauth/start', 'POST', { provider, visibility });
      const url = safeAuthorizeUrl(r.authorizeUrl);
      if (!url) throw new Error('bad authorize url');
      window.location.assign(url);
    } catch (e) { setError(driveErrorKey(e)); setBusy(null); }
  };
  const disconnect = async (c: ConnectionView) => {
    setError(null);
    try {
      await bffJson(`/v1/connectors/drive/connections/${encodeURIComponent(c.id)}/disconnect`, 'POST');
      if (open?.id === c.id) setOpen(null);
      setNotice(t('connectors.drive.disconnected', { provider: t(`connectors.drive.provider.${c.provider}`) }));
      providers.reload();
      connections.reload();
    } catch (e) { setError(driveErrorKey(e)); }
  };
  const changeVisibility = async (c: ConnectionView, next: DriveVisibility) => {
    setError(null);
    try {
      await bff<ConnectionView>(`/v1/connectors/drive/connections/${encodeURIComponent(c.id)}`, { method: 'PATCH', body: JSON.stringify({ visibility: next }) });
      providers.reload();
      connections.reload();
    } catch (e) { setError(driveErrorKey(e)); }
  };

  return (
    <section aria-labelledby="drive-import-title">
      <Heading level={2} id="drive-import-title">{t('connectors.drive.title')}</Heading>
      <Text tone="muted">{t('connectors.drive.intro')}</Text>
      {connected && (DRIVES as readonly string[]).includes(connected) ? <Text role="status">{t('connectors.drive.connected_notice', { provider: t(`connectors.drive.provider.${connected}`) })}</Text> : null}
      {notice ? <Text role="status">{notice}</Text> : null}
      {error ? <div role="alert"><ErrorState message={t(error)} /></div> : null}
      <fieldset>
        <legend>{t('connectors.drive.visibility.legend')}</legend>
        <label>
          <input type="radio" name="drive-visibility" value="private" checked={visibility === 'private'} onChange={() => setVisibility('private')} />
          {' '}{t('connectors.drive.visibility.private')}
        </label>
        <label>
          <input type="radio" name="drive-visibility" value="workspace" checked={visibility === 'workspace'} onChange={() => setVisibility('workspace')} />
          {' '}{t('connectors.drive.visibility.workspace')}
        </label>
      </fieldset>
      <ul>
        {list.map((p) => {
          const name = t(`connectors.drive.provider.${p.provider}`);
          const c = p.connection;
          const ready = c?.status === 'connected';
          return (
            <li key={p.provider}>
              <Button variant={ready ? 'primary' : 'secondary'} disabled={!p.configured || busy === p.provider}
                onClick={() => (ready ? setOpen(c) : void connect(p.provider))}>
                {c && !ready ? t('connectors.drive.reconnect', { provider: name }) : t(`connectors.drive.import_from.${p.provider}`)}
              </Button>
              {!p.configured ? <Text tone="muted">{t('connectors.drive.not_configured', { provider: name })}</Text> : null}
              {c ? (
                <Text tone="muted">
                  {c.displayName ? <bdi>{t('connectors.drive.connected_as', { name: f.text(c.displayName) })} </bdi> : null}
                  <Toggle checked={c.visibility === 'workspace'} onChange={(on) => void changeVisibility(c, on ? 'workspace' : 'private')}
                    label={t('connectors.drive.visibility.toggle', { provider: name })} />
                  {' '}{t(c.visibility === 'workspace' ? 'connectors.drive.visibility.workspace' : 'connectors.drive.visibility.private')}
                  <Button onClick={() => void disconnect(c)}>{t('connectors.drive.disconnect', { provider: name })}</Button>
                </Text>
              ) : null}
            </li>
          );
        })}
      </ul>
      <Text tone="muted">{t('connectors.drive.scopes_note')}</Text>
      {shared.length ? (
        <section aria-labelledby="drive-shared-title">
          <Heading level={3} id="drive-shared-title">{t('connectors.drive.shared.title')}</Heading>
          <ul>
            {shared.map((c) => (
              <li key={c.id}>
                <Text>{t('connectors.drive.shared.row', { provider: t(`connectors.drive.provider.${c.provider}`), name: f.text(c.connectedBy ?? '') })}</Text>
                <Button disabled={c.status !== 'connected'} onClick={() => setOpen(c)}>{t('connectors.drive.shared.browse')}</Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {open ? (
        <DrivePicker connection={open} onClose={() => setOpen(null)} onReconnect={() => void connect(open.provider)}
          onQueued={(n) => { setNotice(t('connectors.drive.imports.queued', { count: String(n) })); setImportsTick((x) => x + 1); onImported(); }} />
      ) : null}
      <ImportProgress tick={importsTick} onChange={onImported} />
    </section>
  );
}

function DrivePicker({ connection, onClose, onQueued, onReconnect }: { connection: ConnectionView; onClose: () => void; onQueued: (n: number) => void; onReconnect: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const [trail, setTrail] = useState<{ id: string | null; name: string }[]>([{ id: null, name: '' }]);
  const [items, setItems] = useState<ItemView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, ItemView>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const importKey = useRef(newImportKey());
  const folder = trail[trail.length - 1]!.id;
  const provider = t(`connectors.drive.provider.${connection.provider}`);

  const load = async (more: string | null) => {
    setPhase(more ? 'ready' : 'loading');
    const q = new URLSearchParams({ pageSize: '50' });
    if (folder) q.set('folderId', folder);
    if (more) q.set('cursor', more);
    try {
      const page = await bff<{ items: ItemView[]; nextCursor: string | null }>(`/v1/connectors/drive/connections/${encodeURIComponent(connection.id)}/items?${q.toString()}`);
      setItems((prev) => (more ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setPhase('ready');
    } catch (e) { setError(driveErrorKey(e)); setPhase('error'); }
  };
  useEffect(() => { void load(null); }, [folder, connection.id]);

  const toggle = (i: ItemView, on: boolean) => {
    importKey.current = newImportKey();
    setSelected((s) => { const n = new Map(s); if (on) n.set(i.id, i); else n.delete(i.id); return n; });
  };
  const ids = useMemo(() => [...selected.keys()], [selected]);
  const submit = async () => {
    if (!ids.length) return;
    setSubmitting(true); setError(null);
    try {
      const out = await bff<{ imports: unknown[] }>(`/v1/connectors/drive/connections/${encodeURIComponent(connection.id)}/import`, {
        method: 'POST', body: JSON.stringify({ fileIds: ids }), headers: { 'idempotency-key': importKey.current },
      });
      importKey.current = newImportKey();
      setSelected(new Map());
      onQueued(out.imports.length);
    } catch (e) { setError(driveErrorKey(e)); }
    setSubmitting(false);
  };

  const columns: Column<ItemView>[] = [
    {
      key: 'select', header: t('connectors.drive.col.select'),
      render: (i) => (i.kind === 'file' ? (
        <input type="checkbox" id={`pick-${i.id}`} aria-label={t('connectors.drive.picker.select', { name: i.name })} disabled={!i.importable}
          checked={selected.has(i.id)} onChange={(ev) => toggle(i, ev.target.checked)} />
      ) : null),
    },
    {
      key: 'name', header: t('connectors.drive.col.name'),
      render: (i) => (i.kind === 'folder'
        ? <Button onClick={() => setTrail((tr) => [...tr, { id: i.id, name: i.name }])} aria-label={t('connectors.drive.picker.open_folder', { name: i.name })}><bdi>{f.text(i.name)}</bdi></Button>
        : <bdi>{f.text(i.name)}</bdi>),
    },
    { key: 'size', header: t('connectors.drive.col.size'), render: (i) => sizeLabel(i.sizeBytes, f.number) },
    { key: 'modified', header: t('connectors.drive.col.modified'), render: (i) => f.date(i.modifiedAt) },
    {
      key: 'note', header: t('connectors.drive.col.note'),
      render: (i) => (i.importable ? (i.importAs === 'application/pdf' && i.mimeType !== 'application/pdf' ? t('connectors.drive.picker.converted') : '') : (i.kind === 'folder' ? t('connectors.drive.picker.folder') : t(reasonKey(i.reason)))),
    },
  ];

  return (
    <section aria-labelledby="drive-picker-title">
      <Heading level={3} id="drive-picker-title">{t('connectors.drive.picker.title', { provider })}</Heading>
      <nav aria-label={t('connectors.drive.picker.path')}>
        {trail.map((c, n) => (
          <Button key={`${c.id ?? 'root'}-${n}`} disabled={n === trail.length - 1} onClick={() => setTrail((tr) => tr.slice(0, n + 1))}>
            {c.id ? <bdi>{f.text(c.name)}</bdi> : t('connectors.drive.picker.root')}
          </Button>
        ))}
      </nav>
      {error ? (
        <div role="alert">
          <ErrorState message={t(error)} />
          {error === 'connectors.drive.error.reconnect_needed' ? <Button onClick={onReconnect}>{t('connectors.drive.reconnect', { provider })}</Button> : null}
        </div>
      ) : null}
      {phase === 'loading' ? <Loading label={t('common.loading')} /> : null}
      {phase === 'ready' && !items.length ? <EmptyState title={t('connectors.drive.picker.empty')} /> : null}
      {phase === 'ready' && items.length ? <DataTable caption={t('connectors.drive.picker.title', { provider })} columns={columns} rows={items} /> : null}
      {phase === 'ready' && cursor ? <Button onClick={() => void load(cursor)}>{t('connectors.drive.picker.load_more')}</Button> : null}
      <div>
        <Button variant="primary" disabled={!ids.length || submitting} onClick={() => void submit()}>{t('connectors.drive.picker.import_selected', { count: String(ids.length) })}</Button>
        <Button onClick={onClose}>{t('connectors.drive.picker.close')}</Button>
      </div>
    </section>
  );
}

const ACTIVE = new Set(['queued', 'fetching']);
const tone = (s: ImportView['status']) => (s === 'completed' ? 'success' : s === 'rejected' || s === 'failed' ? 'error' : 'neutral');

function ImportProgress({ tick, onChange }: { tick: number; onChange: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const [rows, setRows] = useState<ImportView[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const wasActive = useRef(false);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const data = await bff<ImportView[]>('/v1/connectors/drive/imports?limit=20');
        if (!live) return;
        setRows(data);
        const active = data.some((r) => ACTIVE.has(r.status));
        if (wasActive.current && !active) onChange(); // jobs finished: refresh the library list
        wasActive.current = active;
        if (active) timer = setTimeout(() => void poll(), 3000);
      } catch { /* progress is best effort */ }
    };
    void poll();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [tick]);

  const resync = async (r: ImportView) => {
    setMessage(null);
    try {
      const out = await bffJson<{ changed: boolean }>(`/v1/connectors/drive/imports/${encodeURIComponent(r.id)}/resync`, 'POST');
      setMessage(t(out.changed ? 'connectors.drive.imports.resync_queued' : 'connectors.drive.imports.up_to_date'));
      if (out.changed) wasActive.current = true;
    } catch (e) { setMessage(t(driveErrorKey(e))); }
  };

  if (!rows?.length) return null;
  const columns: Column<ImportView>[] = [
    { key: 'file', header: t('connectors.drive.col.name'), render: (r) => <bdi>{f.text(r.fileName)}</bdi> },
    { key: 'provider', header: t('connectors.drive.col.source'), render: (r) => t(`connectors.drive.provider.${r.provider}`) },
    { key: 'status', header: t('connectors.drive.col.status'), render: (r) => <Badge tone={tone(r.status)}>{t(`connectors.drive.imports.status.${r.status}`)}</Badge> },
    { key: 'note', header: t('connectors.drive.col.note'), render: (r) => (r.reason ? t(reasonKey(r.reason)) : '') },
    { key: 'when', header: t('connectors.drive.col.modified'), render: (r) => f.dateTime(r.createdAt) },
    { key: 'action', header: t('connectors.drive.col.action'), render: (r) => (r.status === 'completed' ? <Button onClick={() => void resync(r)}>{t('connectors.drive.imports.resync')}</Button> : null) },
  ];
  return (
    <section aria-labelledby="drive-imports-title">
      <Heading level={3} id="drive-imports-title">{t('connectors.drive.imports.title')}</Heading>
      {message ? <Text role="status">{message}</Text> : null}
      <DataTable caption={t('connectors.drive.imports.title')} columns={columns} rows={rows} />
    </section>
  );
}
